import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo } from 'react';
import { parse12HourTime, format12HourTime } from '../../utils/datetime.js';
import { cn } from '../../utils/formatters.js';
import '@radix-ui/react-label';
import './alert.js';
import '@radix-ui/react-avatar';
import './badge.js';
import 'lucide-react';
import 'react-day-picker';
import './button.js';
import './chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import 'cmdk';
import '@radix-ui/react-dialog';
import '@radix-ui/react-popover';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import '@radix-ui/react-progress';
import './radio-group.js';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './select.js';
import '@radix-ui/react-separator';
import './sidebar.js';
import 'react-toastify';
import '@radix-ui/react-switch';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';
import '@clerk/clerk-react';
import '../../lib/toast.js';
import 'react-router-dom';
import '../../contexts/AppContext.js';
import '../../contexts/OrganizationContext.js';
import 'axios';
import '@tanstack/react-query';
import '../page-layout/FormPageLayout.js';
import '../form/RichTextEditor.js';
import '../crud/CrudDetailView.js';
import '../crud/CrudFormView.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import '@dnd-kit/core';
import '@dnd-kit/sortable';
import '@dnd-kit/utilities';

/**
 * Three-cell time picker — pick (don't type) the hour, minute, and
 * period via shadcn `Select`s, styled as a single input group:
 *
 *   ┌─────┬──────┬─────┐
 *   │  3 ▼│ 30 ▼ │ PM ▼│
 *   └─────┴──────┴─────┘
 *
 * Canonical I/O is 24-hour `"HH:mm"` so callers can serialize / use
 * the value with their own timezone logic.
 *
 * Minute options are in 5-minute increments by default to keep the
 * dropdown scannable. If the bound value is off-grid (e.g. an older
 * record saved with `:37`), the current minute is injected so it
 * stays selectable rather than silently snapping to a 5-minute mark.
 */
const HOURS = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
function TimeInput({ value, onChange, disabled, className, minuteStep = 5, }) {
    const { hour12, minute, period } = parse12HourTime(value);
    const minuteOptions = useMemo(() => {
        const base = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep).filter((m) => m < 60);
        // Inject any off-grid minute so old data doesn't snap on open.
        if (minute === null || base.includes(minute))
            return base;
        return [...base, minute].sort((a, b) => a - b);
    }, [minute, minuteStep]);
    const emit = (nextHour, nextMinute, nextPeriod) => {
        onChange(format12HourTime(nextHour, nextMinute, nextPeriod));
    };
    return (jsxs("div", { className: cn('inline-flex items-stretch divide-x rounded-md border border-input bg-background', 'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]', disabled && 'cursor-not-allowed opacity-50', className), children: [jsx(TimeCell, { ariaLabel: "Hours", value: hour12 === null ? undefined : String(hour12), placeholder: "hh", disabled: disabled, onChange: (v) => emit(Number(v), minute ?? 0, period), className: "w-14", items: HOURS.map((h) => ({ value: String(h), label: String(h) })) }), jsx(TimeCell, { ariaLabel: "Minutes", value: minute === null ? undefined : String(minute), placeholder: "mm", disabled: disabled, onChange: (v) => emit(hour12 ?? 12, Number(v), period), className: "w-16", items: minuteOptions.map((m) => ({
                    value: String(m),
                    label: String(m).padStart(2, '0'),
                })) }), jsx(TimeCell, { ariaLabel: "Period", value: period, disabled: disabled, onChange: (v) => emit(hour12, minute, v), className: "w-16", items: [
                    { value: 'AM', label: 'AM' },
                    { value: 'PM', label: 'PM' },
                ] })] }));
}
function TimeCell({ ariaLabel, value, placeholder, disabled, onChange, className, items, }) {
    return (jsxs(Select, { value: value, onValueChange: onChange, disabled: disabled, children: [jsx(SelectTrigger, { "aria-label": ariaLabel, className: cn(
                // Strip the cell's own border / ring — the wrapping
                // input-group border is the visual frame.
                'h-9 rounded-none border-0 bg-transparent px-2 shadow-none', 'focus:ring-0 focus-visible:ring-0', 'tabular-nums', 'first:rounded-l-md last:rounded-r-md', className), children: jsx(SelectValue, { placeholder: placeholder }) }), jsx(SelectContent, { className: "max-h-72", children: items.map((it) => (jsx(SelectItem, { value: it.value, children: it.label }, it.value))) })] }));
}

export { TimeInput };
//# sourceMappingURL=time-input.js.map
